/**
 * @fileoverview Payment Success Page - Celebration & Next Steps
 * @module PaymentSuccessPage
 * 
 * Design Philosophy:
 * - Consistent with LandingPage brand aesthetic and header design
 * - Celebratory but professional tone
 * - Clear next steps and value confirmation
 * - Smooth transition back to learning experience
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  CheckCircle2,
  Star,
  ArrowRight,
  Download,
  Sparkles,
  Clock,
  FileText,
  Bot,
  Shield
} from 'lucide-react';

interface SubscriptionDetails {
  tier: string;
  plan: string;
  amount: number;
  nextBilling: string;
  features: string[];
}

const PaymentSuccessPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [subscriptionDetails, setSubscriptionDetails] = useState<SubscriptionDetails | null>(null);

  // Get session ID from URL parameters
  const sessionId = searchParams.get('session_id');

  const Logo = () => (
    <div className="flex items-center gap-3">
      <img 
        src="/transparent-640-logo.png" 
        alt="Appeal Your Home" 
        className="w-10 h-10 object-contain"
      />
      <div className="flex flex-col">
        <h1 className="text-lg font-extrabold text-white tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          APPEAL YOUR HOME
        </h1>
        <p className="text-xs text-gray-300 font-medium tracking-wide" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Learn. Advocate. Save.
        </p>
      </div>
    </div>
  );

  useEffect(() => {
    // Load Montserrat font
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    
    const fetchSubscriptionDetails = async () => {
      if (!sessionId) {
        // No session ID means user navigated here directly - go to dashboard
        navigate('/dashboard');
        return;
      }

      try {
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        setSubscriptionDetails({
          tier: 'AI-Enhanced',
          plan: 'Monthly',
          amount: 97,
          nextBilling: '2025-02-07',
          features: [
            'Complete 7-module video course',
            'AI-generated appeal documents',
            'Personalized property analysis',
            'AI-powered comparable research',
            'Priority email support',
            'Live Q&A session access'
          ]
        });

        setLoading(false);
      } catch (error) {
        console.error('Error fetching subscription details:', error);
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
    
    return () => {
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, [sessionId, navigate]);

  const handleContinueToDashboard = () => {
    navigate('/dashboard');
  };

  const handleDownloadInvoice = () => {
    console.log('Downloading invoice...');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-white flex items-center justify-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Processing your payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* Header - Matches LandingPage Navigation */}
      <nav className="sticky top-0 w-full bg-gray-900/95 backdrop-blur-sm shadow-lg z-50 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-20">
            <Logo />
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Success Hero */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-8">
            <div className="w-24 h-24 bg-green-50 border-2 border-green-200 rounded-full flex items-center justify-center shadow-lg">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
          </div>

          <h1 className="text-4xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>Payment Successful!</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Welcome to {subscriptionDetails?.tier || 'your new plan'}! You now have access to all features to maximize your property tax savings.
          </p>
        </div>

        {/* Subscription Details Card */}
        {subscriptionDetails && (
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-10 mb-12">
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Sparkles className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>{subscriptionDetails.tier} Plan</h2>
                </div>
                <p className="text-gray-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>{subscriptionDetails.plan} Billing</p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-extrabold text-blue-600 mb-1" style={{ fontFamily: 'Montserrat, sans-serif' }}>${subscriptionDetails.amount}</div>
                <p className="text-gray-500 text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>per month</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Plan Features */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <Star className="w-5 h-5 text-blue-600" />
                  <span>Your Features</span>
                </h3>
                <div className="space-y-3">
                  {subscriptionDetails.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Billing Info */}
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center space-x-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  <Clock className="w-5 h-5 text-purple-600" />
                  <span>Billing Information</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Next billing date:</span>
                    <span className="text-gray-900 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>{subscriptionDetails.nextBilling}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>Amount:</span>
                    <span className="text-gray-900 font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>${subscriptionDetails.amount}.00</span>
                  </div>
                  <button
                    onClick={handleDownloadInvoice}
                    className="flex items-center space-x-2 text-blue-600 hover:text-blue-500 transition-colors font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Invoice</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Next Steps */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 hover:shadow-2xl transition-all duration-300">
            <div className="w-12 h-12 bg-blue-50 border border-blue-100 rounded-lg flex items-center justify-center mb-4">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Start Learning</h3>
            <p className="text-gray-600 text-sm leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Access your complete course curriculum and begin your property tax appeal journey.
            </p>
          </div>

          {subscriptionDetails?.tier === 'AI-Enhanced' && (
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 hover:shadow-2xl transition-all duration-300">
              <div className="w-12 h-12 bg-purple-50 border border-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Bot className="w-6 h-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>AI Features</h3>
              <p className="text-gray-600 text-sm leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Your AI-powered tools are now available for document generation and property analysis.
              </p>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 hover:shadow-2xl transition-all duration-300">
            <div className="w-12 h-12 bg-green-50 border border-green-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Support Access</h3>
            <p className="text-gray-600 text-sm leading-relaxed font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Get help when you need it with email support and our resource library.
            </p>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={handleContinueToDashboard}
            className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg hover:shadow-xl" style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <span>Continue to Dashboard</span>
            <ArrowRight className="w-5 h-5" />
          </button>
          
          <button
            onClick={() => navigate('/step/1/document')}
            className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-200 transition-all duration-300 border border-gray-300" style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Start First Module
          </button>
        </div>

        {/* Footer Note */}
        <div className="text-center mt-12">
          <p className="text-gray-500 text-sm font-medium" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Questions about your subscription? Contact support at support@appealyourhome.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage; 